import { useMemo } from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { Title } from '../../../api/types/corporate';

/** Read-only visual org-chart rendering of the title hierarchy — an additional view alongside
 *  TitleListPage's flat indented table (see that file's flattenTree for the DataGrid/tree-view
 *  incompatibility rationale; this component is built from plain MUI primitives for the same
 *  reason). Deliberately duplicated from the department module's identically-shaped
 *  OrgChartView rather than shared, to keep the two module boundaries independent.
 *
 *  Connector lines are drawn with the classic CSS-only "tree of nested <ul>/<li>" trick: each
 *  <li> draws two half-width top-border segments (via ::before/::after) that meet at its
 *  horizontal center, and each nested <ul> draws a short vertical drop from its parent.
 *  Adjacent siblings' half-lines join into one continuous line above the row, and the first/
 *  last child in a row trims its outer half so the line doesn't overshoot the group — all
 *  without knowing any node's pixel width up front, so it holds up for arbitrarily wide/deep
 *  trees. No canvas/SVG dependency involved.
 */

interface TitleNode {
  title: Title;
  children: TitleNode[];
}

/** Nests the parent-linked flat list into a tree. Mirrors TitleListPage's flattenTree orphan
 *  handling: a row whose parentTitleId points at a missing/already-visited id is still
 *  surfaced, as an extra root, rather than silently dropped. */
function buildTree(rows: Title[]): TitleNode[] {
  const byParent = new Map<number | null, Title[]>();
  for (const row of rows) {
    const key = row.parentTitleId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(row);
  }
  const visited = new Set<number>();

  function build(parentId: number | null): TitleNode[] {
    const children = (byParent.get(parentId) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    const nodes: TitleNode[] = [];
    for (const child of children) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      nodes.push({ title: child, children: build(child.id) });
    }
    return nodes;
  }

  const roots = build(null);
  for (const row of rows) {
    if (!visited.has(row.id)) {
      visited.add(row.id);
      roots.push({ title: row, children: build(row.id) });
    }
  }
  return roots;
}

const CONNECTOR_COLOR = 'divider' as const;

const treeListSx = {
  display: 'flex',
  position: 'relative',
  m: 0,
  p: 0,
  pt: '20px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '50%',
    width: 0,
    height: '20px',
    borderLeft: '2px solid',
    borderLeftColor: CONNECTOR_COLOR,
  },
} as const;

const treeItemSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  listStyle: 'none',
  position: 'relative',
  px: 1.5,
  pt: '20px',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: '50%',
    width: '50%',
    height: '20px',
    borderTop: '2px solid',
    borderTopColor: CONNECTOR_COLOR,
  },
  '&::after': {
    right: 'auto',
    left: '50%',
    borderLeft: '2px solid',
    borderLeftColor: CONNECTOR_COLOR,
  },
  '&:only-child': { pt: 0 },
  '&:only-child::before, &:only-child::after': { display: 'none' },
  '&:first-of-type::before': { borderTopColor: 'transparent' },
  '&:last-of-type::after': { borderLeftColor: 'transparent' },
  '&:last-of-type::before': {
    borderRight: '2px solid',
    borderRightColor: CONNECTOR_COLOR,
    borderTopRightRadius: 6,
  },
  '&:first-of-type::after': { borderTopLeftRadius: 6 },
} as const;

function TitleNodeCard({ title, onClick }: { title: Title; onClick?: (title: Title) => void }) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick ? () => onClick(title) : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        px: 2,
        py: 1,
        minWidth: 150,
        maxWidth: 220,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': onClick ? { boxShadow: 3, borderColor: 'primary.main' } : undefined,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center', wordBreak: 'break-word' }}>
        {title.name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Seviye {title.level}
      </Typography>
      <Stack direction="row" spacing={0.5}>
        <Chip size="small" color={title.isActive ? 'success' : 'default'} label={title.isActive ? 'Aktif' : 'Pasif'} />
        {title.canViewSubordinates && <Chip size="small" variant="outlined" label="Astları Görür" />}
      </Stack>
    </Paper>
  );
}

function OrgChartNode({ node, onClick }: { node: TitleNode; onClick?: (title: Title) => void }) {
  return (
    <Box component="li" sx={treeItemSx}>
      <TitleNodeCard title={node.title} onClick={onClick} />
      {node.children.length > 0 && (
        <Box component="ul" sx={treeListSx}>
          {node.children.map((child) => (
            <OrgChartNode key={child.title.id} node={child} onClick={onClick} />
          ))}
        </Box>
      )}
    </Box>
  );
}

export function TitleOrgChartView({ rows, onNodeClick }: { rows: Title[]; onNodeClick?: (title: Title) => void }) {
  const roots = useMemo(() => buildTree(rows), [rows]);

  if (roots.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        Kayıtlı unvan bulunamadı.
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto', width: '100%', py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, width: 'fit-content', mx: 'auto' }}>
        {roots.map((root) => (
          <Box key={root.title.id} component="ul" sx={{ display: 'flex', m: 0, p: 0 }}>
            <OrgChartNode node={root} onClick={onNodeClick} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
